import axios from 'axios';
import Cookies from 'js-cookie';
import { jsonp } from '@ifeng-fe/ui_base';
import { COMMENT_BASE_PATH, COMMENT_APP_ID, COMMENT_API_URL } from '@src/public/constants';
import { getToken } from '@src/public/utils/app';

// 评论axios实例
export const request = axios.create();

// 评论axios实例的基础路径
request.defaults.baseURL = COMMENT_BASE_PATH;

// 评论axios实例的请求拦截器
request.interceptors.request.use(
    config => {
        // 获取用户token
        const token = getToken();
        // 评论axios实例的自定义header
        const headers = {
            Authorization: `Bearer ${token}`,
            commentAppId: COMMENT_APP_ID,
        };

        return { ...config, headers: { ...config.headers, ...headers } };
    },
    err => Promise.reject(err),
);

/** 一级评论列表固定按热度排序，避免首屏与加载更多顺序不一致 */
const COMMENT_LIST_ORDERBY = 'integral';
const COMMENT_LIST_ORDER = 'desc';

/**
 * 将大评论系统字段映射为页面现有结构
 * 子评论取 children，列表预览最多保留 2 条
 */
const transformCommentItem = (item, subjectId, { previewChildren = true } = {}) => {
    if (!item) {
        return null;
    }

    const childrenComments = item.children?.comments || [];
    const childCount = item.children?.count ?? childrenComments.length;
    const createTime = Number(item.create_time);
    const updateTime = Number(item.last_modtime || item.create_time);
    const previewList = previewChildren ? childrenComments.slice(0, 2) : childrenComments;

    return {
        id: String(item.comment_id),
        comment: item.comment_contents || '',
        commentUserImgSrc: item.faceurl || '',
        commentUserName: item.uname || '',
        userLikeStatus: 0,
        likeCount: Number(item.uptimes) || 0,
        subjectId,
        checkStatus: 1,
        createTime: Number.isFinite(createTime) ? createTime * 1000 : Date.now(),
        updateTime: Number.isFinite(updateTime) ? updateTime * 1000 : Date.now(),
        hideExtra: false,
        commentUserId: item.user_id || '',
        rootId: !item.main_id || String(item.main_id) === '0' ? '0' : String(item.main_id),
        pid: item.quote_id != null ? String(item.quote_id) : '0',
        comments: previewList.map(child => transformCommentItem(child, subjectId, { previewChildren: false })).filter(Boolean),
        commentCount: Number(childCount) || 0,
    };
};

/**
 * 评论列表（v3）
 * @see http://doc.staff.ifeng.com/apidoc/comment/#api-01_Comment-GetCommentsV3
 * @param {string} docUrl 文章 doc_url，如 ucms_${id}
 * @param {string} subjectId 主体id（兼容旧调用）
 * @param {number} page 页码，从1开始
 * @param {number} pageSize 每页条数
 */
export const getCommentList = async ({
    docUrl,
    subjectId,
    page = 1,
    pageSize = 10,
}) => {
    const callbackName = 'timelineCommentListCallback';
    const data = {
        doc_url: docUrl || (subjectId ? `ucms_${subjectId}` : ''),
        p: page,
        pagesize: pageSize,
        orderby: COMMENT_LIST_ORDERBY,
        order: COMMENT_LIST_ORDER,
        format: 'js',
    };

    const res = await jsonp(`${COMMENT_API_URL}/v3/get/comments`, {
        data,
        jsonpCallback: callbackName,
        cache: false,
    });

    const result = res?.comments !== undefined ? res : res?.data || {};
    const comments = Array.isArray(result.comments) ? result.comments : [];
    const list = comments.map(item => transformCommentItem(item, subjectId)).filter(Boolean);
    const total = Number(result.count) || list.length;

    // 兼容原 axios + queryCommentForPage 的返回形态
    return {
        status: 200,
        data: {
            code: 200,
            data: {
                pagination: {
                    current: page,
                    pageSize,
                    total,
                },
                list,
            },
        },
    };
};

/**
 * 子评论列表（v3）
 * @see http://doc.staff.ifeng.com/apidoc/comment/#api-01_Comment-GetChildren
 * @param {string} docUrl 文章 doc_url
 * @param {string} commentId 父评论 id
 * @param {string} subjectId 主体id（兼容旧调用）
 * @param {number} page 页码
 * @param {number} pageSize 每页条数
 * @param {string} order 排序 asc/desc
 */
export const getCommentChildren = async ({
    docUrl,
    commentId,
    subjectId,
    page = 1,
    pageSize = 20,
    order = 'asc',
}) => {
    const callbackName = 'timelineCommentChildrenCallback';
    const data = {
        doc_url: docUrl || (subjectId ? `ucms_${subjectId}` : ''),
        comment_id: commentId,
        p: page,
        pagesize: pageSize,
        order,
        format: 'js',
    };

    const res = await jsonp(`${COMMENT_API_URL}/v3/get/children`, {
        data,
        jsonpCallback: callbackName,
        cache: false,
    });

    const result = res?.comments !== undefined ? res : res?.data || {};
    const comments = Array.isArray(result.comments) ? result.comments : [];
    const list = comments
        .map(item => transformCommentItem(item, subjectId, { previewChildren: false }))
        .filter(Boolean);
    const total = Number(result.count) || list.length;

    return {
        status: 200,
        data: {
            code: 200,
            data: {
                pagination: {
                    current: page,
                    pageSize,
                    total,
                },
                list,
            },
        },
    };
};

/**
 * 评论点赞（顶）
 * @see comment vote.php
 * @param {string} commentId 评论 id
 * @param {string} docUrl 文章 doc_url，如 ucms_${id}
 * @param {string} subjectId 主体id（兼容旧调用）
 */
export const postLikeComment = ({ commentId, docUrl, subjectId }) => {
    const callbackName = 'timelineCommentVoteCallback';

    return jsonp(`${COMMENT_API_URL}/vote.php`, {
        data: {
            cmtId: commentId,
            job: 'up',
            docUrl: docUrl || (subjectId ? `ucms_${subjectId}` : ''),
            format: 'js',
        },
        jsonpCallback: callbackName,
        cache: false,
    });
};

/**
 * 删除评论
 * @see http://doc.staff.ifeng.com/apidoc/comment/#api-01_Comment-delete
 * @param {string} commentId 评论id
 */
export const postDeleteComment = ({ commentId }) => {
    const callbackName = 'timelineCommentDeleteCallback';

    return jsonp(`${COMMENT_API_URL}/v3/delete/comment`, {
        data: {
            comment_id: commentId,
            sid: getToken(),
            format: 'js',
        },
        jsonpCallback: callbackName,
        cache: false,
    });
};

/**
 * 发布评论（大评论系统）
 * @see http://doc.staff.ifeng.com/apidoc/comment/#api-post-post
 * @param {string} comment 评论内容
 * @param {string} docUrl 文章 doc_url，如 ucms_${id}
 * @param {string} docName 文章标题
 * @param {string} skey 校验值
 * @param {string} [quoteId] 父级评论 id（回复时传入）
 */
export const postInsertComment = ({ comment, docUrl, docName, skey, quoteId }) => {
    const callbackName = 'timelineCommentPostCallback';
    const data = {
        doc_url: docUrl,
        doc_name: docName,
        content: comment,
        skey: skey || '',
        from: 'pc',
        format: 'js',
        sid: getToken(),
    };

    if (quoteId && quoteId !== '0') {
        data.quote_id = quoteId;
    }

    return jsonp(`${COMMENT_API_URL}/post`, {
        data,
        jsonpCallback: callbackName,
        cache: false,
    });
};

/**
 * 获取单条评论数据
 * @see http://doc.staff.ifeng.com/apidoc/comment/#api-01_Comment-GetComment
 * @param {string} commentId 评论id
 * @param {string} subjectId 主体id（兼容旧调用，用于字段映射）
 */
export const getCommentById = async ({ commentId, subjectId }) => {
    const callbackName = 'timelineCommentDetailCallback';
    const res = await jsonp(`${COMMENT_API_URL}/get/comment`, {
        data: {
            comment_id: commentId,
            format: 'js',
        },
        jsonpCallback: callbackName,
        cache: false,
    });

    // 接口可能返回对象、数组或空数组
    const rawItem = Array.isArray(res) ? res[0] : res?.comment_id ? res : res?.data;
    const data = transformCommentItem(rawItem, subjectId);

    return {
        status: 200,
        data: {
            code: data ? 200 : 0,
            data,
        },
    };
};
